import React, { useEffect, useRef } from 'react';
import { StarDivider } from './Ornaments';

// Gold points on the plate, as % of plate width/height. size: 1 small → 3 large
const TWINKLES = [
  { x: 49.02, y: 26.81, size: 3.2, dur: 9, delay: 0.6 },
  { x: 49.1, y: 35.66, size: 2.2, dur: 10, delay: 4.4 },
  { x: 48.96, y: 9.59, size: 1.8, dur: 12, delay: 7.8 },
  { x: 67.1, y: 44.25, size: 1.6, dur: 9.5, delay: 8.1 },
  { x: 18.93, y: 40.04, size: 1.6, dur: 11, delay: 2.4 },
  { x: 49.18, y: 80.13, size: 1.4, dur: 12.5, delay: 10.2 },
  { x: 27.73, y: 29.71, size: 1.4, dur: 10.5, delay: 6.3 },
  { x: 37.03, y: 17.11, size: 1.2, dur: 11.5, delay: 3.6 },
  { x: 64.18, y: 60.15, size: 1.2, dur: 12, delay: 11.1 },
  { x: 77.34, y: 24.96, size: 1.2, dur: 10, delay: 1.5 },
  { x: 60.45, y: 16.96, size: 1.2, dur: 13, delay: 7.2 },
  { x: 12.27, y: 18.93, size: 1.2, dur: 11, delay: 9.4 },
  { x: 14.78, y: 35.02, size: 1, dur: 12, delay: 5.1 },
  { x: 37.16, y: 33.31, size: 1, dur: 11, delay: 12.7 },
  { x: 18.94, y: 44.51, size: 1, dur: 13, delay: 0.2 },
  { x: 85.5, y: 35.44, size: 1, dur: 12.5, delay: 8.9 },
  // gold threads on the figure and staff — micro glints
  { x: 57.76, y: 48.96, size: 1.1, dur: 8, delay: 3.1 },
  { x: 42.93, y: 48.89, size: 1.1, dur: 8.5, delay: 6.6 },
  { x: 55.66, y: 56.12, size: 1, dur: 9, delay: 1.9 },
  { x: 49.43, y: 52.28, size: 0.9, dur: 8.5, delay: 10.8 },
  { x: 47.37, y: 57.84, size: 0.9, dur: 9.5, delay: 5.7 },
];

const Twinkle = ({ x, y, size, dur, delay }) => (
  <svg
    viewBox="0 0 24 24"
    className="hero-twinkle"
    style={{ left: `${x}%`, top: `${y}%`, '--tw-size': size, '--tw-dur': `${dur}s`, '--tw-delay': `${delay}s` }}
    aria-hidden
  >
    <circle cx="12" cy="12" r="7" fill="url(#tw-glow)" />
    <path d="M12 0 C12.6 8 16 11.4 24 12 C16 12.6 12.6 16 12 24 C11.4 16 8 12.6 0 12 C8 11.4 11.4 8 12 0 Z" fill="#F6DFA6" />
    <path d="M12 5 C12.3 10 14 11.7 19 12 C14 12.3 12.3 14 12 19 C11.7 14 10 12.3 5 12 C10 11.7 11.7 10 12 5 Z" fill="#FFF8E6" transform="rotate(45 12 12)" opacity="0.8" />
    <circle cx="12" cy="12" r="1.8" fill="#FFFFFF" />
  </svg>
);

const usePlateParallax = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = Math.min(window.scrollY, window.innerHeight);
      el.style.setProperty('--scroll', `${y}px`);
      el.style.setProperty('--scroll-p', String(y / window.innerHeight));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [ref]);
};

export const Hero = ({ onDescend }) => {
  const heroRef = useRef(null);
  usePlateParallax(heroRef);

  return (
    <section id="top" ref={heroRef} className="hero" data-testid="hero-section">
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <radialGradient id="tw-glow">
            <stop offset="0%" stopColor="#F3D692" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#F3D692" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>

      <div className="hero-sky-group" aria-hidden>
        <div className="hero-sky" style={{ backgroundImage: "url('/realms/hero-sky.webp')" }} />
        <div className="hero-stars hero-stars--a" />
        <div className="hero-stars hero-stars--b" />
        <div className="hero-lines" />
      </div>

      <div className="hero-ext-group" aria-hidden>
        <img src="/realms/hero-ext-left.webp" alt="" className="hero-ext hero-ext--left" draggable="false" />
        <img src="/realms/hero-ext-right.webp" alt="" className="hero-ext hero-ext--right" draggable="false" />
      </div>

      <div className="hero-plate" data-testid="hero-plate">
        <img src="/realms/hero-plate.webp" alt="Ikarus, landed, walking toward the celestial city" className="hero-plate-img" draggable="false" data-testid="hero-figure" />
        <img src="/realms/hero-gold.webp" alt="" className="hero-gold hero-gold--halo" draggable="false" aria-hidden />
        <img src="/realms/hero-gold.webp" alt="" className="hero-gold hero-gold--base" draggable="false" aria-hidden />
        <img src="/realms/hero-gold.webp" alt="" className="hero-gold hero-gold--glint" draggable="false" aria-hidden />
        {TWINKLES.map((t) => <Twinkle key={`${t.x}-${t.y}`} {...t} />)}

        <h1 className="hero-title hero-reveal" style={{ '--d': '700ms' }} data-testid="hero-title">
          It begins with a <br />boy who landed.
        </h1>

        <div className="hero-bottom hero-reveal" style={{ '--d': '1000ms' }}>
          <p className="hero-wordmark" data-testid="hero-wordmark">IKARUS</p>
          <p className="hero-wordmark-sub"><span /><span>LANDED</span><span /></p>
          <StarDivider className="hero-divider" />
          <p className="hero-tagline" data-testid="hero-tagline">
            Artifacts based on <br />human encounters <br />with the divine.
          </p>
        </div>
      </div>

      <div className="hero-descend-wrap">
        <button type="button" onClick={onDescend} className="hero-descend hero-reveal" style={{ '--d': '1600ms' }} aria-label="Descend to the four realms" data-testid="hero-descend-btn">
          <span>Descend</span>
          <span className="hero-descend-track"><span className="hero-descend-dot" /></span>
        </button>
      </div>
    </section>
  );
};
